import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import { useCrmMyAccess, useCrmSourceOptions } from '@/hooks/queries';
import { useCrmSalespersonAccess, useGrantSalespersonAccess, useRevokeSalespersonAccess } from '@/hooks/queries/useCrmSalespersonAccess';
import { useToast } from '@/hooks/useToast';

export default function CrmSettingsPage() {
  const { toasts, showToast, hideToast } = useToast();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newSourceValue, setNewSourceValue] = useState('');

  // Check if user is admin
  const { data: myAccess, isLoading: accessLoading } = useCrmMyAccess();
  const { data: accessData, isLoading: dataLoading } = useCrmSalespersonAccess();
  const { data: sourceOptionsData } = useCrmSourceOptions();

  const grantMutation = useGrantSalespersonAccess();
  const revokeMutation = useRevokeSalespersonAccess();

  const sourceOptions = sourceOptionsData?.options || [];
  const groupedAccess = accessData?.groupedByUser || [];

  // Redirect non-admins
  if (!accessLoading && !myAccess?.isAdmin) {
    return <Navigate to="/crm" replace />;
  }

  const handleAddAccess = async (e) => {
    e.preventDefault();
    if (!newEmail || !newSourceValue) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    try {
      await grantMutation.mutateAsync({ email: newEmail, sourceValue: newSourceValue });
      showToast('Access granted successfully', 'success');
      setNewEmail('');
      setNewSourceValue('');
      setShowAddModal(false);
    } catch (error) {
      showToast(error.message || 'Failed to grant access', 'error');
    }
  };

  const handleRevokeAccess = async (accessId, userEmail, sourceLabel) => {
    if (!confirm(`Remove ${userEmail}'s access to "${sourceLabel}"?`)) return;

    try {
      await revokeMutation.mutateAsync(accessId);
      showToast('Access revoked', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to revoke access', 'error');
    }
  };

  const isLoading = accessLoading || dataLoading;

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-gray-900">CRM Settings</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium text-sm"
          >
            + Add Salesperson
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Manage salesperson access. Salespeople can only see contacts and opportunities matching their assigned sources.
        </p>
      </div>

      {/* Salesperson Access Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-4">
                  <div className="h-10 bg-gray-200 rounded w-1/3"></div>
                  <div className="flex-1 h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : groupedAccess.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No salespeople configured yet</p>
            <p className="text-sm text-gray-400 mb-4">
              Add a salesperson to give them limited CRM access based on contact sources
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
            >
              Add your first salesperson
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Assigned Sources</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groupedAccess.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-700 text-sm font-medium">
                            {user.userEmail?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">{user.userEmail}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {user.sources.map((source) => (
                          <span
                            key={source.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
                          >
                            {source.value}
                            <button
                              onClick={() => handleRevokeAccess(source.id, user.userEmail, source.value)}
                              className="text-blue-500 hover:text-red-600 ml-1"
                              title="Remove this source"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setNewEmail(user.userEmail);
                          setShowAddModal(true);
                        }}
                        className="text-zapt-purple hover:text-zapt-purple/80 text-sm font-medium"
                      >
                        Add Source
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Grant Salesperson Access
              </h3>
              <form onSubmit={handleAddAccess}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="salesperson@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      If user doesn't exist, they will be created with 'invited' status
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source
                    </label>
                    <select
                      value={newSourceValue}
                      onChange={(e) => setNewSourceValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
                      required
                    >
                      <option value="">Select a source...</option>
                      {sourceOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.value}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      They will only see contacts/opportunities with this source
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setNewEmail('');
                      setNewSourceValue('');
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={grantMutation.isPending}
                    className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50"
                  >
                    {grantMutation.isPending ? 'Granting...' : 'Grant Access'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}
